'use client';

import { useEffect, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import clsx from 'clsx';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useModal } from '@/components/ModalContext';
import { getZipcodeValidationStatus } from '@/app/api/zipcodeChecker/zipcodeChecker';
import { US_ZIP_REGEX } from '@/lib/zipcode/validateZipcode';
import rte, { isRte } from '@/utils/rte';
import CTAButton from '@/components/elements/CTAButton';
import CtaArrowIcon from '@/components/icons/CtaArrowIcon';

const COOKIE_NAME = 'zipcode';
const COOKIE_DAYS = 7;

const FaCheckIcon = ({ className = '' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 448 512"
    className={className}
    aria-hidden="true"
  >
    <path
      fill="currentColor"
      d="M439 105c12 13 12 33 0 46L183 407a32 32 0 0 1-46 0L9 279a32 32 0 1 1 46-46l104 106 234-234c13-12 33-12 46 0z"
    />
  </svg>
);

const SuccessBadge = ({ className = '' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="16"
    viewBox="0 0 12 16"
    className={className}
    aria-hidden="true"
  >
    <circle cx="6" cy="9.66675" r="6" fill="#168F47" />
    <path
      d="M8.5 8.16675L4.9799 11.6064L3.16813 9.45842"
      stroke="white"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ErrorBadge = ({ className = '' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="13"
    viewBox="0 0 12 13"
    className={className}
    aria-hidden="true"
  >
    <circle cx="6" cy="6.66675" r="6" fill="#C62626" />
    <path
      d="M7.45841 4.74387C7.62926 4.57302 7.90627 4.57302 8.07713 4.74387C8.24798 4.91473 8.24798 5.19174 8.07713 5.36259L4.4532 8.98651C4.28235 9.15737 4.00534 9.15737 3.83449 8.98651C3.66363 8.81566 3.66363 8.53865 3.83449 8.3678L7.45841 4.74387Z"
      fill="white"
    />
    <path
      d="M8.16551 8.3678C8.33637 8.53865 8.33637 8.81566 8.16551 8.98651C7.99466 9.15737 7.71765 9.15737 7.5468 8.98651L3.92287 5.36259C3.75202 5.19174 3.75202 4.91473 3.92287 4.74387C4.09373 4.57302 4.37074 4.57302 4.54159 4.74387L8.16551 8.3678Z"
      fill="white"
    />
  </svg>
);

const PillButton = ({ label, onClick, disabled }) => (
  <CTAButton
    variant="outline"
    onClick={onClick}
    disabled={disabled}
    className="shrink-0 px-6 py-3 text-sm capitalize"
  >
    {label}
  </CTAButton>
);

// BL-784: the V2 comp renders "Check Zip Code"/"Edit Zip Code" as a sea-blue
// text link with a trailing arrow and a 2px underline bar sized to the text,
// not a pill button — V1's PillButton stays as-is for the SliceZone variant.
// The arrow is CtaArrowIcon (the project's standard CTA arrow) rather than a
// plain "→" character, to match the Figma arrow glyph exactly.
const ZipLinkCta = ({ label, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="group flex cursor-pointer flex-col items-center gap-1 py-2 disabled:cursor-not-allowed disabled:opacity-50"
  >
    <span className="flex items-center text-base text-sea">
      {label}
      <CtaArrowIcon />
    </span>
    <span className="h-0.5 w-full bg-sea" aria-hidden="true" />
  </button>
);

const LearnMoreModal = ({ content }) => {
  const html = isRte(content) ? rte(content) : null;

  if (!html) return null;

  return (
    <div
      className="rte-content rte-content--light text-center text-primary text-base px-2 py-4 space-y-4"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

// BL-652: buy_box_card has its own `show_zip_code_checker_expanded` toggle to
// control whether this starts open or collapsed, passed in as `initialExpanded`
// — every other usage renders through SliceZone's generic block map and never
// passes this, so it keeps defaulting to collapsed.
// BL-652: `collapsible` is opt-in and defaults off, so the SliceZone
// `zip_code_checker` block V1 renders keeps its static heading. The V2 buy box
// card turns it on: Helix's widget makes the whole heading row the toggle.
// BL-784: comp specs heroicons-outline/chevron-down at 16px, rotated 180°
// open — swapped in for the hand-drawn 18x10 chevron this used to be.
const ZipCodeChecker = ({
  block,
  initialExpanded = false,
  collapsible = false,
  onExpandedChange,
}) => {
  const headingText = block?.heading || 'In-Home Setup & Removal Available';

  const calloutsList = Array.isArray(block?.success_content_list_items)
    ? block.success_content_list_items.filter(Boolean)
    : [];

  const learnMoreContent = isRte(block?.content) ? block.content : null;
  const hasLearnMore = Boolean(learnMoreContent);

  const { openModal } = useModal();

  // BL-652 review (2026-09-04): two separate things, deliberately. `open` is the
  // disclosure state the chevron drives in the collapsible variant; `editMode`
  // is whether the form is showing. Conflating them is what made the toggle
  // invert after a successful check — see the note on `showForm` below.
  const [open, setOpen] = useState(initialExpanded);
  const [editMode, setEditMode] = useState(initialExpanded);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [zipcode, setZipcode] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    const saved = Cookies.get(COOKIE_NAME);

    if (saved && US_ZIP_REGEX.test(saved.trim())) {
      setZipcode(saved);
      setSuccess(true);
    }
  }, []);

  // BL-652 review (2026-09-04): collapsed follows `open` and nothing else, so
  // one click on the chevron is always exactly one state change.
  //
  // The previous attempt folded `success` into this (`!editMode && !success`)
  // to stop the box collapsing past a fresh result. It fixed that symptom and
  // created a worse one: verifyZip() leaves `editMode` false on success, so the
  // box was open while the toggle — which read `editMode` — thought it was
  // closed. The chevron pointed the wrong way, the click meant to close instead
  // ran enableEditMode() (reopening the form and wiping the result), and only
  // the second click collapsed it. Helix keeps these separate the same way: its
  // body is `x-show="editMode"` while the input has its own `hideZipCodeInput`.
  const collapsed = collapsible && !open;

  // Whether the ZIP input itself is on screen. Helix's `!hideZipCodeInput`:
  // once a ZIP is verified the field gives way to the result, and "Edit ZIP
  // Code" brings it back. V1 has no disclosure, so there `showForm` is just
  // `editMode` and every V1 code path below behaves exactly as it did.
  const showForm = collapsible ? open && !success : editMode;

  // Helix renders the callout list on success, inside the open box, right under
  // the "Available in <zip>!" line — the three reassurances about what in-home
  // setup actually includes. V1 shows the same list in its own resting state
  // instead, so the two variants each keep their own trigger.
  const showCallouts = collapsible ? !collapsed && success : !showForm;

  const focusInputSoon = () => {
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const enableEditMode = () => {
    setEditMode(true);
    setError(false);
    setSuccess(false);
    focusInputSoon();
  };

  const failVerification = () => {
    setError(true);
    setEditMode(true);
    setSuccess(false);
  };

  const verifyZip = async () => {
    const cleaned = (zipcode || '').toString().replace(/\s+/g, '');

    if (!cleaned) {
      failVerification();
      return;
    }

    // BL-983: the route owns validation (lib/zipcode/validateZipcode) so every
    // attempt, including malformed and out-of-area ones, reaches the Datadog log.
    setLoading(true);
    try {
      const result = await getZipcodeValidationStatus(cleaned);

      if (!result?.success) {
        failVerification();
        return;
      }

      Cookies.set(COOKIE_NAME, cleaned, { expires: COOKIE_DAYS, sameSite: 'lax' });
      setSuccess(true);
      setError(false);
      setEditMode(false);
    } catch (err) {
      console.error('Error validating zip code:', err);
      failVerification();
    } finally {
      setLoading(false);
    }
  };

  // BL-652 review (2026-09-04): keyed to what is actually on screen, not to
  // `editMode`. In the collapsible variant the form can be visible with
  // `editMode` still false (the user opened the box without going through
  // enableEditMode), and this used to swallow that first click.
  const triggerCta = () => {
    if (loading) return;
    if (showForm) {
      verifyZip();
    } else {
      enableEditMode();
    }
  };

  const handleOpenLearnMore = () => {
    if (!hasLearnMore) return;
    openModal(<LearnMoreModal content={learnMoreContent} />, 'max-w-2xl');
  };

  const buttonLabel = loading
    ? 'Checking...'
    : success
      ? 'Edit ZIP Code'
      : 'Check ZIP Code';
  const submitDisabled = loading || (showForm && zipcode.trim().length === 0);

  const headingIcon = block?.icon?.url ? (
    /* eslint-disable-next-line @next/next/no-img-element */
    <img
      src={block.icon.url}
      alt={block.icon.description || ''}
      width={block.icon.dimension?.width || 120}
      height={block.icon.dimension?.height || 120}
      className="w-[37px] h-auto"
    />
  ) : null;

  return (
    <div data-name="zip-code-checker" className="flex flex-col gap-2">
      <div className={clsx(error && 'bg-[#C62626]')}>
        <div
          className={clsx(
            'flex items-center justify-between gap-4 bg-white',
            // BL-784: comp specs a 1px sand border with 16px padding on every
            // side; V1's heavier sand-light border/padding is unchanged.
            collapsible ? 'border border-sand p-4' : 'border-2 border-sand-light px-6 py-4',
          )}
        >
          <div className="flex h-full w-full flex-col justify-center space-y-2 text-primary">
            {collapsible ? (
              <button
                type="button"
                onClick={() => {
                  const next = !open;

                  setOpen(next);
                  // BL-652 (2026-09-04): the V2 buy box card renders the pitstop
                  // as a sibling and folds it away with this box, the way Helix
                  // does. It owns that decision, so it only needs to be told
                  // when the disclosure moves — the state itself stays here.
                  onExpandedChange?.(next);
                  // Opening onto the empty form should land the caret in it,
                  // which is what enableEditMode() used to do here. Opening onto
                  // a result must not, or it would steal focus from the page.
                  if (next && !success) focusInputSoon();
                }}
                aria-expanded={open}
                className="flex w-full cursor-pointer items-center justify-between gap-4 text-sm font-medium"
              >
                {/* BL-784: comp's V2 heading row has no leading icon — V1 keeps headingIcon below. */}
                <span className="text-left">{headingText}</span>
                <ChevronDownIcon
                  aria-hidden="true"
                  className={clsx('h-4 w-4 shrink-0 text-primary transition-transform', open && 'rotate-180')}
                />
              </button>
            ) : (
              <div className="flex w-full items-center gap-4 text-sm font-medium">
                {headingIcon}
                <span>{headingText}</span>
              </div>
            )}

            {/* BL-784: the "Available in <zip>!" line stays visible even
                collapsed per the comp's "Closed - Accepted Zip" state
                (4300:33151) — only the checklist/edit-link collapse. */}
            {success && (
              <div className="flex items-center gap-2 text-sm text-[#168F47]">
                <SuccessBadge className="w-3 shrink-0" />
                <span>{`Available in ${zipcode}!`}</span>
              </div>
            )}

            {showCallouts && calloutsList.length > 0 && (
              <div className="flex w-full flex-col text-sm">
                {calloutsList.map((callout, i) => {
                  const text = callout?.single_line_text;

                  if (!text) return null;

                  return (
                    <div
                      key={callout?._metadata?.uid || i}
                      className="flex items-center gap-2 text-sm"
                    >
                      <span className="text-[#2E8462]">
                        <FaCheckIcon className="w-3" />
                      </span>
                      <span>{text}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* BL-652 review (2026-09-04): a verified ZIP hides the field, so
                without this the collapsible variant had no way back to it —
                the widget dead-ended on its own result. Helix keeps its CTA
                mounted for exactly this, relabelled "Edit ZIP Code". */}
            {collapsible && !collapsed && success && (
              <div className="flex w-full flex-col items-start gap-2">
                <ZipLinkCta
                  label={buttonLabel}
                  onClick={triggerCta}
                  disabled={submitDisabled}
                />
              </div>
            )}

            {showForm && (
              <div className="flex w-full flex-col items-start gap-2">
                {/* BL-652 review: on a phone the input and the button do not
                    share a row comfortably — the field compresses and the button
                    crowds the edge. Stacked below md, side by side from md up.
                    `items-start` keeps the button hugging its label instead of
                    stretching, which is what the review screenshot shows.
                    BL-784: the V2 comp stacks the input and its link CTA at
                    every width, so collapsible skips the md:flex-row switch. */}
                <div className={clsx('flex w-full flex-col items-start gap-2', !collapsible && 'md:flex-row md:items-stretch')}>
                  <label htmlFor="zipcode-input" className="sr-only">ZIP Code</label>
                  <input
                    id="zipcode-input"
                    ref={inputRef}
                    type="text"
                    inputMode="numeric"
                    autoComplete="postal-code"
                    maxLength={10}
                    value={zipcode}
                    onChange={(e) => {
                      const sanitized = e.target.value.replace(/[^0-9-]/g, '');

                      setZipcode(sanitized);
                      if (error) setError(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        triggerCta();
                      }
                    }}
                    placeholder="Enter ZIP Code"
                    className={clsx(
                      'w-full focus:outline-none focus:ring-0',
                      collapsible
                        ? 'pl-4 pr-3 py-3 text-base font-normal text-primary'
                        : 'px-3 py-3 text-base font-medium text-[#444]',
                      error
                        ? 'border-2 border-[#C62626] focus:border-[#C62626]'
                        : collapsible
                          ? 'border-2 border-sand focus:border-sand'
                          : 'border-2 border-primary focus:border-primary',
                    )}
                  />
                  {!collapsible && (
                    <PillButton
                      label={buttonLabel}
                      onClick={triggerCta}
                      disabled={submitDisabled}
                    />
                  )}
                </div>

                {error && (
                  // BL-784: comp specs 12px for this line (was text-sm/14px) — V1 unchanged.
                  <div className={clsx('flex items-center gap-2 text-[#C62626]', collapsible ? 'text-xs' : 'text-sm')}>
                    <ErrorBadge className="w-[12px] shrink-0" />
                    <span>Services not available in your area.</span>
                  </div>
                )}

                {/* BL-784: comp stacks the CTA below the error message, not
                    beside the input — moved out of the row above for V2. */}
                {collapsible && (
                  <ZipLinkCta
                    label={buttonLabel}
                    onClick={triggerCta}
                    disabled={submitDisabled}
                  />
                )}

                {!collapsible && hasLearnMore && (
                  <button
                    type="button"
                    onClick={handleOpenLearnMore}
                    className="cursor-pointer text-sm text-[#444] underline"
                  >
                    Learn more
                  </button>
                )}
              </div>
            )}
          </div>

          {!showForm && !collapsible && (
            <PillButton
              label={buttonLabel}
              onClick={triggerCta}
              disabled={submitDisabled}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ZipCodeChecker;
